/**
 * Host-based routing helpers — power per-merchant subdomains.
 *
 * Architecture (post tenant-domain refactor):
 *
 *   `app.novixone.ai`            → merchant dashboard (auth-gated)
 *   `{slug}.novixone.shop`       → public tenant pages (storefront,
 *                                   booking, member portal)
 *
 * Local dev mirrors the same shape via the `*.localhost` convention
 * (RFC 6761 — every modern browser auto-resolves `<anything>.localhost`
 * to 127.0.0.1 with no `/etc/hosts` config):
 *
 *   `app.localhost:3000`         → dashboard
 *   `localhost:3000`             → dashboard (bare, kept for legacy)
 *   `{slug}.localhost:3000`      → tenant pages
 *
 * Why a separate `.shop` TLD instead of `*.novixone.ai`:
 *
 *   1. Public Suffix List eligibility — `novixone.shop` can be added
 *      to publicsuffix.org so each tenant subdomain is a fully-isolated
 *      eTLD+1 in the browser security model. Cookies / localStorage /
 *      Service Workers cannot leak across tenants. `novixone.ai` is the
 *      corporate domain and shouldn't be on the PSL.
 *
 *   2. Brand layering — corporate `novixone.ai` ≠ tenant `novixone.shop`
 *      mirrors Shopify's `shopify.com` ≠ `myshopify.com` separation.
 *
 *   3. Email reputation isolation — a misbehaving merchant tenant on
 *      `.shop` can't damage the corporate `.ai` sender reputation.
 *
 *   4. SEO independence — each tenant ranks separately, no cross-pull
 *      with corporate brand search.
 */

/**
 * Slugs the platform reserves so a merchant can never claim a name
 * that would shadow our infrastructure (`app.novixone.shop`, etc.) or
 * be confused for a system route. Single source of truth — both the
 * signup-time validator and proxy.ts rejecting routing import this.
 *
 * Categories:
 *   - Platform hosts:    app, api, dashboard, dev, staging, beta, alpha
 *   - Mail / DNS infra:  mail, smtp, pop, imap, mx, ns1, ns2
 *   - Tools / endpoints: auth, login, signup, settings, webhook, status
 *   - Brand:             novixone, novix, novixstar
 *   - Common content:    about, contact, blog, docs, help, legal,
 *                        privacy, terms, support, pricing, security
 *   - Path namespaces:   m, r, mobile, public, pay
 *   - Reserved infra:    cdn, static, media, files, assets, www
 */
export const RESERVED_SLUGS: ReadonlySet<string> = new Set([
  // Platform hosts
  'app', 'api', 'admin', 'dashboard', 'dev', 'staging', 'prod',
  'beta', 'alpha', 'preview', 'sandbox', 'qa', 'test', 'demo',
  // Mail / DNS infra
  'mail', 'smtp', 'pop', 'imap', 'mx', 'ns1', 'ns2', 'webmail',
  // Auth / endpoints
  'auth', 'login', 'signup', 'signin', 'logout', 'reset', 'verify',
  'oauth', 'sso',
  // System surfaces
  'settings', 'account', 'billing', 'webhook', 'webhooks', 'status',
  'health', 'metrics', 'monitor',
  // Brand
  'novixone', 'novix', 'novixstar', 'novixchat',
  // Path namespaces (existing routes)
  'm', 'r', 'mobile', 'public', 'pay', 'invite', 'onboarding',
  'get-started', 'checkout',
  // Content
  'about', 'contact', 'blog', 'docs', 'help', 'legal', 'privacy',
  'terms', 'support', 'pricing', 'security', 'changelog', 'careers',
  'press', 'partners', 'api-docs',
  // Infra subdomains
  'cdn', 'static', 'media', 'files', 'assets', 'img', 'images',
  'fonts', 'video', 'audio', 'storage', 'edge', 'cache',
  // Common short reserves
  'www', 'root', 'public', 'private', 'internal', 'system',
])

const SLUG_RE = /^[a-z][a-z0-9-]{2,39}$/

/**
 * Production tenant TLD. Local dev uses `*.localhost:<port>`.
 * The shop suffix can be overridden per-environment via
 * `NEXT_PUBLIC_TENANT_HOST_ROOT` to support staging hosts like
 * `*.staging.novixone.shop`.
 */
const PROD_TENANT_ROOT = 'novixone.shop'

export function getTenantHostRoot(): string {
  const env = process.env.NEXT_PUBLIC_TENANT_HOST_ROOT
  if (env && env.length > 0) return env
  return PROD_TENANT_ROOT
}

/**
 * Build an absolute customer-facing tenant URL. Single source of truth
 * for the `{slug}.{root}` shape — receipts, emails, dashboard previews,
 * Web Chat allowlists all go through this so the URL never drifts.
 *
 *   getTenantUrl('lumina')              → 'https://lumina.novixone.shop'
 *   getTenantUrl('lumina', '/book')     → 'https://lumina.novixone.shop/book'
 *   getTenantUrl('lumina', '/member')   → 'http://lumina.localhost:3000/member' (dev)
 *
 * Localhost roots use http://, everything else https://. The slug is
 * NOT validated here — the caller is expected to have run it through
 * the signup-time regex; a malformed slug yields a URL that 404s,
 * which is the desired failure mode.
 */
export function getTenantUrl(slug: string, path: string = ''): string {
  const root = getTenantHostRoot()
  const proto = root.startsWith('localhost') ? 'http' : 'https'
  const suffix = path ? (path.startsWith('/') ? path : `/${path}`) : ''
  return `${proto}://${slug}.${root}${suffix}`
}

/**
 * Returns true when the supplied host header indicates a public
 * tenant request (a `{slug}.<tenant-root>` shape). Strips the port
 * before matching so `lumina.localhost:3000` and `lumina.novixone.shop`
 * both resolve correctly.
 */
function tenantRootFromHost(host: string): string | null {
  const lower = host.toLowerCase()
  // Localhost dev: `<slug>.localhost:3000`
  if (/\.localhost(?::\d+)?$/.test(lower)) return 'localhost'
  // Production / staging: `<slug>.<configured-root>` with optional port
  const root = getTenantHostRoot().toLowerCase()
  if (lower === root || lower === `www.${root}`) return null
  if (lower.endsWith(`.${root}`)) return root
  return null
}

/**
 * Extract the merchant slug from the request host header.
 *
 *   `lumina.novixone.shop`        → 'lumina'
 *   `pinky-nail.localhost:3000`   → 'pinky-nail'
 *   `localhost:3000`              → null (dashboard host)
 *   `app.novixone.ai`             → null (dashboard host)
 *   `app.novixone.shop`           → null (reserved slug, not a tenant)
 *   `evil.com`                    → null
 *
 * The slug is validated against the same regex the signup flow uses.
 * Reserved slugs return null so the proxy can serve a 404 (or fall
 * through to platform routing) instead of treating them as a tenant.
 */
export function resolveTenantSlug(host: string): string | null {
  if (!host) return null
  const root = tenantRootFromHost(host)
  if (!root) return null

  // Strip the trailing `.<root>` and any port from the leftmost label.
  // Localhost can have multiple labels (e.g. `dev.lumina.localhost`)
  // but we treat only the leftmost as the slug.
  const lower = host.toLowerCase().replace(/:\d+$/, '')
  let slug: string
  if (root === 'localhost') {
    slug = lower.replace(/\.localhost$/, '')
  } else {
    slug = lower.slice(0, lower.length - root.length - 1)
  }

  // Multi-label localhost hosts (e.g. `staging.lumina.localhost`) are
  // not a valid single-tenant slug — reject so they don't 500.
  if (slug.includes('.')) return null

  if (!SLUG_RE.test(slug)) return null
  if (RESERVED_SLUGS.has(slug)) return null
  return slug
}

/**
 * Build a tenant-context href.
 *
 * The internal route tree lives at `/m/[slug]/...`, but the customer-
 * visible URL on the tenant subdomain has no `/m/{slug}` prefix (the
 * proxy rewrites it on the way in). Two contexts host tenant pages:
 *
 *   1. **Tenant subdomain** (`{slug}.novixone.shop`, `{slug}.localhost`)
 *      The browser already sees the bare path (`/book`,
 *      `/services/abc`); emitting `/m/{slug}/book` here triggers a
 *      308 canonical redirect on every click — extra round-trip, no
 *      benefit.
 *
 *   2. **Storefront-editor preview iframe** — runs on the dashboard
 *      host (`app.novixone.ai` / `localhost:3000`) loading
 *      `/m/{slug}/?cockpit=1` with the `storefront_preview` cookie.
 *      Bare paths break here because the dashboard host has no `/book`
 *      route; the prefix is required.
 *
 * Pass `onSubdomain` from the page that already knows its host (server
 * component reads `headers().get('host')`; client component reads
 * `window.location.host` and runs it through `resolveTenantSlug`).
 * This helper stays sync + pure so it composes cleanly with React's
 * render path without an extra async boundary.
 *
 *   tenantHref('lumina', '/book',          true)  → '/book'
 *   tenantHref('lumina', '/book',          false) → '/m/lumina/book'
 *   tenantHref('lumina', '',               true)  → '/'
 *   tenantHref('lumina', '',               false) → '/m/lumina'
 *   tenantHref('lumina', '/',              true)  → '/'
 *   tenantHref('lumina', '/',              false) → '/m/lumina'
 *
 * `path` is required to begin with `/` (or be empty / `/` for the
 * tenant root); we don't accept relative segments to avoid silent
 * `/m/lumina/foo` ↔ `/m/luminafoo` mistakes from a missing slash.
 */
export function tenantHref(slug: string, path: string, onSubdomain: boolean): string {
  if (path !== '' && path !== '/' && !path.startsWith('/')) {
    throw new Error(
      `tenantHref: path must start with '/' (got ${JSON.stringify(path)})`,
    )
  }
  if (onSubdomain) {
    return path === '' ? '/' : path
  }
  if (path === '' || path === '/') return `/m/${slug}`
  return `/m/${slug}${path}`
}

/**
 * True for hosts that should serve the merchant dashboard (auth-gated
 * application UI), not a tenant page.
 */
export function isDashboardHost(host: string): boolean {
  if (!host) return false
  const lower = host.toLowerCase()
  // Bare localhost (`localhost:3000`) or the `app.` subdomain in any env.
  if (/^localhost(:\d+)?$/.test(lower)) return true
  if (/^app\.localhost(:\d+)?$/.test(lower)) return true
  if (lower === 'app.novixone.ai') return true
  return false
}
